import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Bot, GraduationCap, Gauge, CheckCircle, Mail, Linkedin, Sparkles, TrendingUp, Zap, Target, Users, Award, ChevronRight, Rocket, Brain, Shield, Globe, Sun, Moon, Code, BarChart, Menu, X, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { mockData } from '../mock';
import { useTheme, useLanguage } from '../context/AppContext';
import { translations } from '../translations';

const Home = () => {
  const { theme, toggleTheme } = useTheme();
  const { language, toggleLanguage } = useLanguage();
  const t = translations[language];

  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [stats, setStats] = useState({ clients: 0, savings: 0, roi: 0 });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;
    let step = 0;

    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      setStats({
        clients: Math.floor(150 * progress),
        savings: Math.floor(60 * progress),
        roi: Math.floor(450 * progress)
      });

      if (step >= steps) clearInterval(timer);
    }, interval);

    return () => clearInterval(timer);
  }, []);

  const serviceIcons = {
    automation: Bot,
    formation: GraduationCap,
    audit: Gauge
  };

  const features = [
    { icon: Brain, title: t.hero.features.aiPowered.title, desc: t.hero.features.aiPowered.desc },
    { icon: Rocket, title: t.hero.features.fastResults.title, desc: t.hero.features.fastResults.desc },
    { icon: Shield, title: t.hero.features.enterprise.title, desc: t.hero.features.enterprise.desc },
    { icon: Globe, title: t.hero.features.global.title, desc: t.hero.features.global.desc }
  ];

  const isDark = theme === 'dark';

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0A0B0F] text-white' : 'bg-white text-slate-900'} relative overflow-hidden transition-colors duration-500`}>
      {/* Advanced animated background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className={`absolute inset-0 ${isDark ? 'opacity-40' : 'opacity-20'}`}>
          <div 
            className="absolute w-[800px] h-[800px] rounded-full blur-3xl animate-mesh-1"
            style={{
              background: `radial-gradient(circle, ${isDark ? 'rgba(57,173,227,0.3)' : 'rgba(57,173,227,0.15)'} 0%, transparent 70%)`,
              top: '10%',
              left: '10%'
            }}
          />
          <div 
            className="absolute w-[600px] h-[600px] rounded-full blur-3xl animate-mesh-2"
            style={{
              background: `radial-gradient(circle, ${isDark ? 'rgba(0,255,209,0.25)' : 'rgba(0,255,209,0.12)'} 0%, transparent 70%)`,
              bottom: '20%',
              right: '15%'
            }}
          />
          <div 
            className="absolute w-[700px] h-[700px] rounded-full blur-3xl animate-mesh-3"
            style={{
              background: `radial-gradient(circle, ${isDark ? 'rgba(7,116,182,0.2)' : 'rgba(7,116,182,0.1)'} 0%, transparent 70%)`,
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)'
            }}
          />
        </div>
        
        {/* Floating Icons and Shapes */}
        <div className={`absolute inset-0 ${isDark ? 'opacity-10' : 'opacity-10'}`}>
          {/* AI Brain Icon */}
          <div className="absolute top-[15%] left-[8%] animate-float-slow">
            <Brain className={`w-16 h-16 ${isDark ? 'text-[#39ADE3]' : 'text-[#0774B6]'}`} />
          </div>
          
          {/* Code Icon */}
          <div className="absolute top-[25%] right-[12%] animate-float-delayed" style={{ animationDelay: '1s' }}>
            <Code className={`w-20 h-20 ${isDark ? 'text-[#00FFD1]' : 'text-[#39ADE3]'}`} />
          </div>
          
          {/* Rocket Icon */}
          <div className="absolute top-[60%] left-[15%] animate-float-slow" style={{ animationDelay: '2s' }}>
            <Rocket className={`w-14 h-14 ${isDark ? 'text-[#39ADE3]' : 'text-[#0774B6]'}`} />
          </div>
          
          {/* Bar Chart Icon */}
          <div className="absolute bottom-[20%] right-[18%] animate-float-delayed" style={{ animationDelay: '1.5s' }}>
            <BarChart className={`w-18 h-18 ${isDark ? 'text-[#00FFD1]' : 'text-[#39ADE3]'}`} />
          </div>
          
          {/* Zap Icon */}
          <div className="absolute top-[40%] right-[8%] animate-float-slow" style={{ animationDelay: '0.5s' }}>
            <Zap className={`w-12 h-12 ${isDark ? 'text-[#39ADE3]' : 'text-[#0774B6]'}`} />
          </div>
          
          {/* Target Icon */}
          <div className="absolute bottom-[35%] left-[10%] animate-float-delayed" style={{ animationDelay: '2.5s' }}>
            <Target className={`w-16 h-16 ${isDark ? 'text-[#00FFD1]' : 'text-[#39ADE3]'}`} />
          </div>
          
          {/* Sparkles Icon */}
          <div className="absolute top-[70%] right-[25%] animate-float-slow" style={{ animationDelay: '1.2s' }}>
            <Sparkles className={`w-14 h-14 ${isDark ? 'text-[#39ADE3]' : 'text-[#0774B6]'}`} />
          </div>
          
          {/* TrendingUp Icon */}
          <div className="absolute top-[50%] left-[5%] animate-float-delayed" style={{ animationDelay: '3s' }}>
            <TrendingUp className={`w-18 h-18 ${isDark ? 'text-[#00FFD1]' : 'text-[#39ADE3]'}`} />
          </div>
        </div>
        
        {/* Geometric Shapes */}
        <div className={`absolute inset-0 ${isDark ? 'opacity-5' : 'opacity-[0.06]'}`}>
          {/* Circles */}
          <div className="absolute top-[20%] right-[20%] w-32 h-32 border-2 border-[#39ADE3] rounded-full animate-spin-slow"></div>
          <div className="absolute bottom-[30%] left-[25%] w-24 h-24 border-2 border-[#00FFD1] rounded-full animate-pulse-slow"></div>
          
          {/* Squares */}
          <div className="absolute top-[45%] right-[30%] w-20 h-20 border-2 border-[#39ADE3] animate-rotate-slow"></div>
          <div className="absolute bottom-[15%] left-[35%] w-28 h-28 border-2 border-[#00FFD1] animate-rotate-reverse"></div>
          
          {/* Triangles */}
          <div className="absolute top-[35%] left-[20%] w-0 h-0 border-l-[20px] border-l-transparent border-r-[20px] border-r-transparent border-b-[35px] border-b-[#39ADE3] animate-float-slow"></div>
          <div className="absolute bottom-[40%] right-[15%] w-0 h-0 border-l-[25px] border-l-transparent border-r-[25px] border-r-transparent border-b-[40px] border-b-[#00FFD1] animate-float-delayed" style={{ animationDelay: '2s' }}></div>
          
          {/* Hexagons (using SVG-like shapes) */}
          <div className="absolute top-[55%] right-[40%] w-16 h-16 animate-pulse-slow" style={{ animationDelay: '1s' }}>
            <svg viewBox="0 0 100 100" className={`w-full h-full ${isDark ? 'stroke-[#39ADE3]' : 'stroke-[#0774B6]'}`} fill="none" strokeWidth="2">
              <polygon points="50,5 95,27.5 95,72.5 50,95 5,72.5 5,27.5" />
            </svg>
          </div>
          <div className="absolute bottom-[25%] left-[40%] w-20 h-20 animate-rotate-slow">
            <svg viewBox="0 0 100 100" className={`w-full h-full ${isDark ? 'stroke-[#00FFD1]' : 'stroke-[#39ADE3]'}`} fill="none" strokeWidth="2">
              <polygon points="50,5 95,27.5 95,72.5 50,95 5,72.5 5,27.5" />
            </svg>
          </div>
        </div>
        
        <div 
          className={`absolute inset-0 ${isDark ? 'opacity-10' : 'opacity-5'}`}
          style={{
            backgroundImage: `
              linear-gradient(${isDark ? 'rgba(57,173,227,0.1)' : 'rgba(57,173,227,0.05)'} 1px, transparent 1px),
              linear-gradient(90deg, ${isDark ? 'rgba(57,173,227,0.1)' : 'rgba(57,173,227,0.05)'} 1px, transparent 1px)
            `,
            backgroundSize: '100px 100px',
            transform: `translateY(${scrollY * 0.5}px)`
          }}
        />
        
        <div 
          className="absolute w-[500px] h-[500px] rounded-full blur-3xl opacity-20 pointer-events-none transition-all duration-300"
          style={{
            background: `radial-gradient(circle, ${isDark ? 'rgba(0,255,209,0.4)' : 'rgba(0,255,209,0.2)'} 0%, transparent 70%)`,
            left: mousePosition.x - 250,
            top: mousePosition.y - 250
          }}
        />
      </div>

      {/* Header */}
      <header className={`fixed top-0 w-full z-50 backdrop-blur-2xl ${isDark ? 'bg-[#0A0B0F]/80 border-white/5' : 'bg-white/80 border-slate-200/50'} border-b transition-colors duration-500`}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-20 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3 group cursor-pointer">
            <div className="relative">
              <div className="absolute inset-0 bg-[#39ADE3] blur-xl opacity-50 group-hover:opacity-100 transition-opacity rounded-full"></div>
              <img 
                src="https://customer-assets.emergentagent.com/job_startgrowthlab/artifacts/1vv64623_IMG_4950.PNG" 
                alt="Start & Growth Logo" 
                className="h-8 sm:h-10 object-contain relative z-10 transition-transform duration-500 group-hover:scale-110"
              />
            </div>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6 lg:gap-10">
            {[t.nav.services, t.nav.portfolio, t.nav.benefits, t.nav.contact].map((item, idx) => (
              <a 
                key={idx}
                href={`#${['services', 'portfolio', 'benefits', 'contact'][idx]}`} 
                className={`${isDark ? 'text-white/70 hover:text-[#00FFD1]' : 'text-slate-600 hover:text-[#0774B6]'} transition-all duration-300 text-sm font-semibold uppercase tracking-wider relative group`}
              >
                {item}
                <span className={`absolute -bottom-2 left-0 w-0 h-0.5 ${isDark ? 'bg-gradient-to-r from-[#00FFD1] to-[#39ADE3]' : 'bg-[#0774B6]'} group-hover:w-full transition-all duration-500`}></span>
              </a>
            ))}
          </nav>
          
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className={`px-3 sm:px-4 h-10 rounded-full font-semibold text-xs sm:text-sm transition-all duration-300 ${
                isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {language === 'en' ? '🇫🇷' : '🇬🇧'}
            </button>
            
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            
            {/* Desktop CTA */}
            <a href="#contact" className="hidden sm:block">
              <Button className="bg-gradient-to-r from-[#0774B6] to-[#39ADE3] text-white hover:from-[#39ADE3] hover:to-[#00FFD1] font-bold px-4 lg:px-6 h-11 rounded-full shadow-lg shadow-[#0774B6]/30 hover:shadow-[#00FFD1]/40 transition-all duration-300 hover:scale-105 border border-white/10 text-sm">
                {t.nav.getStarted}
              </Button>
            </a>
            
            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`md:hidden w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className={`md:hidden ${isDark ? 'bg-[#0A0B0F]/95' : 'bg-white/95'} backdrop-blur-xl border-t ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
            <nav className="container mx-auto px-6 py-6 flex flex-col gap-4">
              {[t.nav.services, t.nav.portfolio, t.nav.benefits, t.nav.contact].map((item, idx) => (
                <a 
                  key={idx}
                  href={`#${['services', 'portfolio', 'benefits', 'contact'][idx]}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`${isDark ? 'text-white hover:text-[#00FFD1]' : 'text-slate-700 hover:text-[#0774B6]'} transition-all duration-300 text-base font-semibold uppercase tracking-wider py-2`}
                >
                  {item}
                </a>
              ))}
              <a href="#contact" onClick={() => setMobileMenuOpen(false)}>
                <Button className="bg-gradient-to-r from-[#0774B6] to-[#39ADE3] text-white hover:from-[#39ADE3] hover:to-[#00FFD1] font-bold px-6 h-12 rounded-full w-full shadow-lg shadow-[#0774B6]/30 transition-all duration-300 mt-4">
                  {t.nav.getStarted}
                </Button>
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 lg:px-20 relative">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Column */}
            <div className="space-y-8 lg:space-y-10">
              <div className={`inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-full ${isDark ? 'bg-white/5 border-white/10' : 'bg-blue-50 border-blue-200'} backdrop-blur-xl border hover:border-[#0774B6] transition-all duration-300 group cursor-pointer shadow-lg`}>
                <Sparkles className={`w-4 h-4 ${isDark ? 'text-[#00FFD1]' : 'text-[#0774B6]'} animate-pulse`} />
                <span className={`text-xs font-bold ${isDark ? 'text-white/90' : 'text-[#0774B6]'} uppercase tracking-wider transition-colors`}>{t.hero.badge}</span>
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black leading-[1.1] tracking-tight">
                <span className={`block ${isDark ? 'text-white' : 'text-slate-900'} hover-3d`}>{t.hero.title1}</span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#39ADE3] via-[#00FFD1] to-[#39ADE3] animate-gradient-x bg-[length:200%_auto] hover-3d">{t.hero.title2}</span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#39ADE3] via-[#00FFD1] to-[#39ADE3] animate-gradient-x bg-[length:200%_auto] hover-3d">{t.hero.title3}</span>
              </h1>

              <p className={`text-base sm:text-lg ${isDark ? 'text-white/70' : 'text-slate-700'} leading-relaxed max-w-xl`}>
                {t.hero.description} {/* <span className={`${isDark ? 'text-[#00FFD1]' : 'text-[#0774B6]'} font-bold text-xl`}>60%</span> */} {t.hero.description2}
              </p>

              <h2 className="block text-3xl sm:text-4xl lg:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#39ADE3] via-[#00FFD1] to-[#39ADE3] animate-gradient-x bg-[length:200%_auto] hover-3d">
              Innovate, Automate and Scale.
              </h2>

              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#contact" className="w-full sm:w-auto">
                  <Button className="bg-gradient-to-r from-[#00FFD1] to-[#39ADE3] text-black hover:from-[#39ADE3] hover:to-[#00FFD1] font-bold px-6 sm:px-8 h-12 sm:h-14 rounded-full text-sm sm:text-base group shadow-2xl shadow-[#00FFD1]/30 hover:shadow-[#00FFD1]/50 transition-all duration-300 hover:scale-105 border-2 border-white/20 w-full">
                    <span>{t.hero.cta1}</span>
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-2 transition-transform duration-300" />
                  </Button>
                </a>
                <a href="#portfolio" className="w-full sm:w-auto">
                  <Button className={`${isDark ? 'bg-white/5 text-white hover:bg-white/10 border-white/20' : 'bg-slate-100 text-slate-700 hover:bg-slate-200 border-slate-200'} backdrop-blur-xl border hover:border-[#39ADE3] font-bold px-6 sm:px-8 h-12 sm:h-14 rounded-full text-sm sm:text-base transition-all duration-300 hover:scale-105 w-full`}>
                    {t.hero.cta2}
                  </Button>
                </a>
              </div>

              {/* Mini stats */}
              {/*<div className="grid grid-cols-3 gap-4 sm:gap-6 pt-4 sm:pt-6">
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#39ADE3]/20 to-transparent rounded-xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <div className="relative text-center">
                    <div className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-transparent bg-clip-text bg-gradient-to-r from-[#39ADE3] to-[#00FFD1]' : 'text-[#0774B6]'}`}>{stats.clients}+</div>
                    <div className={`text-xs ${isDark ? 'text-white/60' : 'text-slate-600'} uppercase tracking-wider font-semibold mt-1`}>{t.hero.stats.clients}</div>
                  </div>
                </div>
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#39ADE3]/20 to-transparent rounded-xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <div className="relative text-center">
                    <div className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-transparent bg-clip-text bg-gradient-to-r from-[#39ADE3] to-[#00FFD1]' : 'text-[#0774B6]'}`}>{stats.savings}%</div>
                    <div className={`text-xs ${isDark ? 'text-white/60' : 'text-slate-600'} uppercase tracking-wider font-semibold mt-1`}>{t.hero.stats.savings}</div>
                  </div>
                </div>
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#39ADE3]/20 to-transparent rounded-xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <div className="relative text-center">
                    <div className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-transparent bg-clip-text bg-gradient-to-r from-[#39ADE3] to-[#00FFD1]' : 'text-[#0774B6]'}`}>{stats.roi}%</div>
                    <div className={`text-xs ${isDark ? 'text-white/60' : 'text-slate-600'} uppercase tracking-wider font-semibold mt-1`}>{t.hero.stats.roi}</div>
                  </div>
                </div>
              </div>*/}
            </div> 

            {/* Right Column - Bento Grid */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {features.map((feature, idx) => {
                const Icon = feature.icon;
                return (
                  <div 
                    key={idx}
                    className={`group relative overflow-hidden rounded-2xl sm:rounded-3xl ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200 shadow-lg'} backdrop-blur-xl border p-4 sm:p-6 hover:border-[#39ADE3]/50 transition-all duration-500 hover:scale-105 cursor-pointer`}
                    style={{
                      animation: `float-up 0.6s ease-out ${idx * 0.1}s both`
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-[#39ADE3]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="relative z-10 space-y-2 sm:space-y-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-gradient-to-br from-[#39ADE3]/20 to-[#00FFD1]/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                        <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${isDark ? 'text-[#00FFD1]' : 'text-[#0774B6]'}`} />
                      </div>
                      <h3 className={`text-sm sm:text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'} ${isDark ? 'group-hover:text-[#00FFD1]' : 'group-hover:text-[#0774B6]'} transition-colors`}>{feature.title}</h3>
                      <p className={`text-xs sm:text-sm ${isDark ? 'text-white/60' : 'text-slate-600'} line-clamp-2`}>{feature.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Event Teaser Section 
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-20 relative">
        <div className="container mx-auto max-w-6xl">
          <Card className={`relative ${isDark ? 'bg-gradient-to-br from-[#12141A] to-[#0A0B0F] border-[#00FFD1]/20' : 'bg-gradient-to-br from-white to-slate-50 border-[#0774B6]/20'} backdrop-blur-xl p-8 sm:p-12 md:p-16 overflow-hidden rounded-3xl border-2 hover:scale-105 transition-all duration-500`}>
            <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-[#00FFD1]/20 to-[#39ADE3]/10 blur-3xl rounded-full"></div>
            
            <div className="relative z-10 grid lg:grid-cols-2 gap-8 items-center">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00FFD1]/10 border border-[#00FFD1]/30 backdrop-blur-xl">
                  <Sparkles className="w-4 h-4 text-[#00FFD1] animate-pulse" />
                  <span className="text-xs font-bold text-[#00FFD1] uppercase tracking-wider">{t.event.badge}</span>
                </div>
                
                <h2 className={`text-4xl sm:text-5xl font-black ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {t.event.title}
                </h2>
                
                <p className={`text-xl ${isDark ? 'text-[#39ADE3]' : 'text-[#0774B6]'} font-semibold`}>
                  {t.event.subtitle}
                </p>
                
                <p className={`text-lg ${isDark ? 'text-white/70' : 'text-slate-600'} leading-relaxed`}>
                  {t.event.tagline}
                </p>
                
                <p className={`text-base ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                  {t.event.description}
                </p>
                <br/>
                <Link to="/event">
                  <Button className="bg-gradient-to-r from-[#00FFD1] to-[#39ADE3] text-black hover:from-[#39ADE3] hover:to-[#00FFD1] font-bold px-8 h-14 rounded-full text-base group shadow-2xl shadow-[#00FFD1]/30 hover:shadow-[#00FFD1]/50 transition-all duration-300 hover:scale-105 border-2 border-white/20">
                    <span>{t.event.cta}</span>
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-2 transition-transform duration-300" />
                  </Button>
                </Link>
              </div>
              
              <div className="relative hidden lg:block">
                <div className="relative w-full aspect-square">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#39ADE3]/30 to-[#00FFD1]/20 rounded-3xl animate-pulse-slow"></div>
                  <div className="absolute inset-8 bg-gradient-to-tr from-[#0774B6]/40 to-[#39ADE3]/30 rounded-3xl animate-float-slow"></div>
                  <div className="absolute inset-16 flex items-center justify-center">
                    <Calendar className={`w-32 h-32 ${isDark ? 'text-[#00FFD1]' : 'text-[#0774B6]'}`} />
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section> */}

            {/* AI Factory Highlight Section */}
            <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-20 relative">
        <div className="container mx-auto max-w-5xl">
          <Card className={`relative ${isDark ? 'bg-gradient-to-br from-[#0774B6]/20 via-[#12141A]/80 to-[#39ADE3]/20 border-[#39ADE3]/30' : 'bg-gradient-to-br from-[#0774B6]/5 via-white to-[#39ADE3]/5 border-[#39ADE3]/40 shadow-2xl'} backdrop-blur-2xl p-8 sm:p-12 rounded-3xl overflow-hidden`}>
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#00FFD1]/20 to-transparent blur-3xl opacity-50"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-[#39ADE3]/20 to-transparent blur-3xl opacity-50"></div>
            
            <div className="relative z-10 text-center">
              <div className={`inline-flex items-center gap-2 px-4 sm:px-5 py-2 rounded-full ${isDark ? 'bg-[#00FFD1]/10 border-[#00FFD1]/20' : 'bg-blue-50 border-blue-200'} border mb-6 backdrop-blur-xl`}>
                <Calendar className={`w-4 h-4 ${isDark ? 'text-[#00FFD1]' : 'text-[#0774B6]'}`} />
                <span className={`text-xs font-bold ${isDark ? 'text-[#00FFD1]' : 'text-[#0774B6]'} uppercase tracking-wider`}>
                  {language === 'en' ? '2026 Program' : 'Programme 2026'}
                </span>
              </div>
              
              <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-black mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                AI FACTORY
              </h2>
              
              <p className={`text-base sm:text-lg mb-8 max-w-2xl mx-auto leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                {language === 'en' 
                  ? 'Exclusive program supporting West African SME leaders in concrete AI adoption. Join 200+ professionals transforming their businesses with AI.' 
                  : 'Programme exclusif accompagnant les dirigeants de PME d\'Afrique de l\'Ouest dans l\'adoption concrète de l\'IA. Rejoignez 200+ professionnels transformant leurs entreprises avec l\'IA.'}
              </p>
              
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/aifactory" data-testid="aifactory-link">
                  <Button 
                    size="lg" 
                    className="bg-gradient-to-r from-[#0774B6] to-[#39ADE3] hover:from-[#0774B6]/90 hover:to-[#39ADE3]/90 text-white rounded-full px-8 py-6 text-base font-bold shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                  >
                    {language === 'en' ? 'Discover the Program' : 'Découvrir le Programme'} <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-20 relative">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12 sm:mb-16 lg:mb-20">
            <div className={`inline-flex items-center gap-2 px-4 sm:px-5 py-2 rounded-full ${isDark ? 'bg-[#39ADE3]/10 border-[#39ADE3]/20' : 'bg-blue-50 border-blue-200'} border mb-4 sm:mb-6 backdrop-blur-xl`}>
              <Target className={`w-4 h-4 ${isDark ? 'text-[#00FFD1]' : 'text-[#0774B6]'}`} />
              <span className={`text-xs font-bold ${isDark ? 'text-[#00FFD1]' : 'text-[#0774B6]'} uppercase tracking-wider`}>{t.services.badge}</span>
            </div>
            <h2 className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 sm:mb-6 ${isDark ? 'text-white' : 'text-slate-900'} px-4`}>{t.services.title}</h2>
            <p className={`text-base sm:text-lg lg:text-xl ${isDark ? 'text-white/60' : 'text-slate-600'} max-w-2xl mx-auto px-4`}>{t.services.subtitle}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {t.services.items.map((service, index) => {
              const Icon = serviceIcons[['automation', 'formation', 'audit'][index]];
              return (
                <div key={index} className="group relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#39ADE3]/20 to-[#00FFD1]/10 opacity-0 group-hover:opacity-100 rounded-2xl sm:rounded-3xl blur-2xl transition-opacity duration-500"></div>
                  <Card className={`relative h-full ${isDark ? 'bg-[#12141A]/80 border-white/10' : 'bg-white border-slate-200 shadow-lg'} backdrop-blur-2xl p-6 sm:p-8 hover:border-[#39ADE3]/50 rounded-2xl sm:rounded-3xl transition-all duration-500 group-hover:scale-105 group-hover:shadow-2xl group-hover:shadow-[#39ADE3]/20 overflow-hidden`}>
                    <div className="absolute top-0 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-gradient-to-br from-[#39ADE3]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-bl-[100px]"></div>
                    
                    <div className="relative space-y-4 sm:space-y-6">
                      <div className="relative w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br from-[#39ADE3]/20 to-[#00FFD1]/10 flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
                        <Icon className={`w-6 h-6 sm:w-8 sm:h-8 ${isDark ? 'text-[#00FFD1] group-hover:text-[#39ADE3]' : 'text-[#0774B6] group-hover:text-[#39ADE3]'} transition-colors duration-500`} />
                      </div>
                      <h3 className={`text-lg sm:text-xl lg:text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'} group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[#39ADE3] group-hover:to-[#00FFD1] transition-all duration-300`}>{service.title}</h3>
                      <p className={`${isDark ? 'text-white/70' : 'text-slate-600'} leading-relaxed text-sm sm:text-base`}>{service.description}</p>
                      <div className="pt-2 sm:pt-4">
                        {/*<div className={`inline-flex items-center gap-2 ${isDark ? 'text-[#00FFD1]' : 'text-[#0774B6]'} group-hover:gap-4 transition-all duration-300 cursor-pointer font-bold text-sm`}>
                          <span className="uppercase tracking-wider">{t.services.learnMore}</span>
                          <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                        </div>*/}
                      </div>
                    </div>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-20 relative">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12 sm:mb-16 lg:mb-20">
            <div className={`inline-flex items-center gap-2 px-4 sm:px-5 py-2 rounded-full ${isDark ? 'bg-[#00FFD1]/10 border-[#00FFD1]/20' : 'bg-blue-50 border-blue-200'} border mb-4 sm:mb-6 backdrop-blur-xl`}>
              <Award className={`w-4 h-4 ${isDark ? 'text-[#00FFD1]' : 'text-[#0774B6]'}`} />
              <span className={`text-xs font-bold ${isDark ? 'text-[#00FFD1]' : 'text-[#0774B6]'} uppercase tracking-wider`}>{t.portfolio.badge}</span>
            </div>
            <h2 className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 sm:mb-6 ${isDark ? 'text-white' : 'text-slate-900'} px-4`}>{t.portfolio.title}</h2>
            <p className={`text-base sm:text-lg lg:text-xl ${isDark ? 'text-white/60' : 'text-slate-600'} max-w-2xl mx-auto px-4`}>{t.portfolio.subtitle}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
            {mockData.portfolio.map((project, index) => (
              <Card 
                key={index} 
                className={`group relative ${isDark ? 'bg-[#12141A]/80 border-white/10' : 'bg-white border-slate-200 shadow-lg'} backdrop-blur-2xl p-5 sm:p-8 lg:p-10 hover:border-[#39ADE3]/50 rounded-2xl sm:rounded-3xl transition-all duration-500 overflow-hidden md:hover:scale-105 hover:shadow-2xl hover:shadow-[#39ADE3]/20`}
              >
                <div className="absolute top-0 right-0 w-32 h-32 sm:w-64 sm:h-64 bg-gradient-to-br from-[#39ADE3]/20 via-[#00FFD1]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-3xl rounded-full"></div>
                
                <div className="relative space-y-4 sm:space-y-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                    <div className="space-y-1 sm:space-y-2">
                      <h3 className={`text-xl sm:text-2xl lg:text-3xl font-black ${isDark ? 'text-white' : 'text-slate-900'} group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[#39ADE3] group-hover:to-[#00FFD1] transition-all duration-300`}>{project.client}</h3>
                      <p className={`${isDark ? 'text-[#39ADE3]' : 'text-[#0774B6]'} text-xs uppercase tracking-widest font-bold`}>{project.industry}</p>
                    </div>
                    <div className="sm:text-right">
                      <div className={`text-lg sm:text-xl lg:text-2xl font-black ${isDark ? 'text-transparent bg-clip-text bg-gradient-to-r from-[#00FFD1] to-[#39ADE3]' : 'text-[#0774B6]'}`}>{project.result}</div>
                      <div className={`text-xs ${isDark ? 'text-white/50' : 'text-slate-500'} uppercase tracking-wider font-semibold mt-1`}>{t.portfolio.impact}</div>
                    </div>
                  </div>
                  
                  {/* Testimonial */}
                  <div className={`${isDark ? 'bg-white/5' : 'bg-slate-50'} p-4 sm:p-6 rounded-xl sm:rounded-2xl border ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                    <div className="flex gap-2 mb-3 sm:mb-4">
                      <svg className={`w-5 h-5 sm:w-6 sm:h-6 ${isDark ? 'text-[#00FFD1]' : 'text-[#0774B6]'} flex-shrink-0`} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                      </svg>
                    </div>
                    <p className={`${isDark ? 'text-white/80' : 'text-slate-700'} leading-relaxed italic mb-3 sm:mb-4 text-sm sm:text-base`}>
                      {project.testimonial}
                    </p>
                    <p className={`text-xs sm:text-sm font-bold ${isDark ? 'text-[#39ADE3]' : 'text-[#0774B6]'}`}>— {project.author}</p>
                  </div>
                  
                  <div className={`pt-4 sm:pt-6 border-t ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                    <div className="flex items-center gap-2 mb-2 sm:mb-3">
                      <div className={`w-1 h-1 rounded-full ${isDark ? 'bg-[#00FFD1]' : 'bg-[#0774B6]'}`}></div>
                      <span className={`font-bold ${isDark ? 'text-white/90' : 'text-slate-700'} text-xs sm:text-sm uppercase tracking-wider`}>{t.portfolio.solution}</span>
                    </div>
                    <p className={`${isDark ? 'text-white/60' : 'text-slate-600'} text-sm sm:text-base`}>{project.solution}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-20 relative">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12 sm:mb-16 lg:mb-20">
            <div className={`inline-flex items-center gap-2 px-4 sm:px-5 py-2 rounded-full ${isDark ? 'bg-[#39ADE3]/10 border-[#39ADE3]/20' : 'bg-blue-50 border-blue-200'} border mb-4 sm:mb-6 backdrop-blur-xl`}>
              <TrendingUp className={`w-4 h-4 ${isDark ? 'text-[#00FFD1]' : 'text-[#0774B6]'}`} />
              <span className={`text-xs font-bold ${isDark ? 'text-[#00FFD1]' : 'text-[#0774B6]'} uppercase tracking-wider`}>{t.benefits.badge}</span>
            </div>
            <h2 className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 sm:mb-6 ${isDark ? 'text-white' : 'text-slate-900'} px-4`}>{t.benefits.title}</h2>
            <p className={`text-base sm:text-lg lg:text-xl ${isDark ? 'text-white/60' : 'text-slate-600'} max-w-2xl mx-auto px-4`}>{t.benefits.subtitle}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12 lg:mb-16">
            {t.benefits.items.map((benefit, index) => (
              <div 
                key={index} 
                className={`group relative p-5 sm:p-6 lg:p-8 ${isDark ? 'bg-[#12141A]/60 border-white/10' : 'bg-white border-slate-200 shadow-lg'} backdrop-blur-xl border hover:border-[#39ADE3]/50 rounded-2xl sm:rounded-3xl transition-all duration-500 hover:bg-opacity-80 cursor-pointer md:hover:scale-105 overflow-hidden`}
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#39ADE3] via-[#00FFD1] to-[#39ADE3] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <CheckCircle className={`w-6 h-6 sm:w-8 sm:h-8 ${isDark ? 'text-[#00FFD1]' : 'text-[#0774B6]'} mb-4 sm:mb-6 group-hover:scale-110 group-hover:rotate-12 transition-all duration-300`} />
                <h3 className={`text-lg sm:text-xl lg:text-2xl font-bold mb-3 sm:mb-4 ${isDark ? 'text-white group-hover:text-[#00FFD1]' : 'text-slate-900 group-hover:text-[#0774B6]'} transition-colors duration-300`}>{benefit.title}</h3>
                <p className={`${isDark ? 'text-white/70' : 'text-slate-600'} leading-relaxed text-sm sm:text-base`}>{benefit.description}</p>
              </div>
            ))}
          </div>

          {/* ROI Showcase */}
          {/*<div className="relative group cursor-pointer">
            <div className="absolute inset-0 bg-gradient-to-r from-[#39ADE3]/20 via-[#00FFD1]/20 to-[#39ADE3]/20 blur-3xl opacity-50 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl"></div>
            <div className={`relative ${isDark ? 'bg-[#12141A]/80' : 'bg-white'} backdrop-blur-2xl border-2 border-[#39ADE3]/50 p-16 rounded-3xl text-center overflow-hidden shadow-2xl`}>
              <div className="absolute inset-0 bg-gradient-to-br from-[#39ADE3]/5 to-transparent"></div>
              <div className="relative">
                <div className={`text-7xl font-black ${isDark ? 'text-transparent bg-clip-text bg-gradient-to-r from-[#39ADE3] via-[#00FFD1] to-[#39ADE3] animate-gradient-x bg-[length:200%_auto]' : 'text-[#0774B6]'} mb-4`}>{t.benefits.roi}</div>
                <p className={`text-xl ${isDark ? 'text-white/80' : 'text-slate-700'}`}>{t.benefits.roiDesc} <span className={`${isDark ? 'text-[#00FFD1]' : 'text-[#0774B6]'} font-bold`}>{t.benefits.roiTime}</span></p>
              </div>
            </div>
          </div>*/}
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-20 relative">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12 sm:mb-16 lg:mb-20">
            <div className={`inline-flex items-center gap-2 px-4 sm:px-5 py-2 rounded-full ${isDark ? 'bg-[#00FFD1]/10 border-[#00FFD1]/20' : 'bg-blue-50 border-blue-200'} border mb-4 sm:mb-6 backdrop-blur-xl`}>
              <Users className={`w-4 h-4 ${isDark ? 'text-[#00FFD1]' : 'text-[#0774B6]'}`} />
              <span className={`text-xs font-bold ${isDark ? 'text-[#00FFD1]' : 'text-[#0774B6]'} uppercase tracking-wider`}>{t.contact.badge}</span>
            </div>
            <h2 className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 sm:mb-6 ${isDark ? 'text-white' : 'text-slate-900'} px-4`}>{t.contact.title}</h2>
            <p className={`text-base sm:text-lg lg:text-xl ${isDark ? 'text-white/60' : 'text-slate-600'} max-w-2xl mx-auto px-4`}>{t.contact.subtitle}</p>
          </div>

          <Card className={`relative ${isDark ? 'bg-[#12141A]/80 border-white/10' : 'bg-white border-slate-200 shadow-2xl'} backdrop-blur-2xl p-6 sm:p-10 md:p-16 overflow-hidden rounded-2xl sm:rounded-3xl`}>
            <div className="absolute top-0 right-0 w-64 h-64 sm:w-96 sm:h-96 bg-gradient-to-br from-[#39ADE3]/20 to-[#00FFD1]/10 blur-3xl rounded-full"></div>
            
            <div className="relative space-y-6 sm:space-y-8 lg:space-y-10 text-center">
              {/* Calendar Icon */}
              <div className="flex justify-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-[#39ADE3]/20 to-[#00FFD1]/10 flex items-center justify-center">
                  <svg className={`w-8 h-8 sm:w-10 sm:h-10 ${isDark ? 'text-[#00FFD1]' : 'text-[#0774B6]'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>

              <div className="space-y-3 sm:space-y-4">
                <h3 className={`text-xl sm:text-2xl lg:text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'} px-2`}>{t.contact.description}</h3>
              </div>

              {/* Features List */}
              <div className={`grid sm:grid-cols-2 gap-3 sm:gap-4 py-6 sm:py-8 ${isDark ? 'text-white/80' : 'text-slate-700'}`}>
                {t.contact.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3 text-left">
                    <CheckCircle className={`w-5 h-5 ${isDark ? 'text-[#00FFD1]' : 'text-[#0774B6]'} flex-shrink-0 mt-0.5`} />
                    <span className="text-sm sm:text-base">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <a 
                href="https://calendar.app.google/6oxKVAdMUCvppBzXA" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-full"
              >
                <Button 
                  className="bg-gradient-to-r from-[#00FFD1] to-[#39ADE3] text-black hover:from-[#39ADE3] hover:to-[#00FFD1] font-black px-6 sm:px-8 md:px-12 h-14 sm:h-16 rounded-full text-sm sm:text-base md:text-lg w-full shadow-2xl shadow-[#00FFD1]/30 hover:shadow-[#00FFD1]/50 transition-all duration-300 hover:scale-105 group uppercase tracking-wide sm:tracking-wider"
                >
                  <span className="line-clamp-1">{t.contact.cta}</span>
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2 flex-shrink-0 group-hover:translate-x-2 transition-transform duration-300" />
                </Button>
              </a>

              {/*<p className={`text-sm ${isDark ? 'text-white/50' : 'text-slate-500'}`}>
                No credit card required • Cancel anytime
              </p>*/}
            </div>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className={`border-t ${isDark ? 'border-white/10' : 'border-slate-200'} py-10 sm:py-12 lg:py-16 px-4 sm:px-6 lg:px-20 relative transition-colors duration-500`}>
        <div className="container mx-auto max-w-7xl">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 lg:gap-12 mb-8 sm:mb-10 lg:mb-12">
            <div className="space-y-4 sm:space-y-6 sm:col-span-2 lg:col-span-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_startgrowthlab/artifacts/1vv64623_IMG_4950.PNG" 
                alt="Start & Growth Logo" 
                className="h-8 sm:h-10 object-contain"
              />
              <p className={`${isDark ? 'text-white/60' : 'text-slate-600'} leading-relaxed max-w-md text-sm sm:text-base`}>{t.footer.description}</p>
              <div className="flex gap-4">
                <a href="mailto:hello@startandgrowth.net" className={`w-10 h-10 sm:w-12 sm:h-12 ${isDark ? 'bg-white/5 border-white/10' : 'bg-slate-100 border-slate-200'} backdrop-blur-sm hover:bg-gradient-to-r hover:from-[#39ADE3] hover:to-[#00FFD1] border rounded-xl sm:rounded-2xl flex items-center justify-center transition-all duration-300 group hover:scale-110`}>
                  <Mail className={`w-4 h-4 sm:w-5 sm:h-5 ${isDark ? 'text-white' : 'text-slate-600'} group-hover:text-white transition-colors`} />
                </a>
                <a href="https://www.linkedin.com/company/startandgrowth-ai-consulting" target="_blank" rel="noopener noreferrer" className={`w-10 h-10 sm:w-12 sm:h-12 ${isDark ? 'bg-white/5 border-white/10' : 'bg-slate-100 border-slate-200'} backdrop-blur-sm hover:bg-gradient-to-r hover:from-[#39ADE3] hover:to-[#00FFD1] border rounded-xl sm:rounded-2xl flex items-center justify-center transition-all duration-300 group hover:scale-110`}>
                  <Linkedin className={`w-4 h-4 sm:w-5 sm:h-5 ${isDark ? 'text-white' : 'text-slate-600'} group-hover:text-white transition-colors`} />
                </a>
              </div>
            </div>
            <div className="space-y-3 sm:space-y-4">
              <h4 className={`text-base sm:text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'} mb-4 sm:mb-6 uppercase tracking-wider`}>{t.footer.quickLinks}</h4>
              <nav className="flex flex-col gap-2 sm:gap-3">
                {[t.nav.services, t.nav.portfolio, t.nav.benefits, t.nav.contact].map((item, idx) => (
                  <a key={idx} href={`#${['services', 'portfolio', 'benefits', 'contact'][idx]}`} className={`${isDark ? 'text-white/60 hover:text-[#00FFD1]' : 'text-slate-600 hover:text-[#0774B6]'} transition-all duration-300 hover:translate-x-2 inline-block text-xs sm:text-sm`}>{item}</a>
                ))}
              </nav>
            </div>
            <div className="space-y-3 sm:space-y-4">
              <h4 className={`text-base sm:text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'} mb-4 sm:mb-6 uppercase tracking-wider`}>{t.footer.services}</h4>
              <nav className="flex flex-col gap-2 sm:gap-3">
                {t.footer.servicesItems.map((item, idx) => (
                  <a key={idx} href="#services" className={`${isDark ? 'text-white/60 hover:text-[#00FFD1]' : 'text-slate-600 hover:text-[#0774B6]'} transition-all duration-300 hover:translate-x-2 inline-block text-xs sm:text-sm`}>{item}</a>
                ))}
              </nav>
            </div>
          </div>
          <div className={`pt-6 sm:pt-8 border-t ${isDark ? 'border-white/10 text-white/50' : 'border-slate-200 text-slate-600'} text-center text-xs sm:text-sm`}>
            <p>{t.footer.copyright.replace('{YEAR}', new Date().getFullYear())}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
